import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import CommonLayout from "../../Common/Layout";
import KMKTable from "../../Common/Table/KMKTable";
import MultiSelectDropDown from "../../Common/MultiSelectDropDown";
import MultiSelect from "../../../util/multiselect";
import useWindowResize from "../../../hooks/useWindowsReszie";
import App from "../../../service/appService";
import Toolbar from "../../Common/Toolbar";
import { convertTimezone } from "../../../util/common";

const useStyles = makeStyles()((theme) => ({
  PanelContainer: {
    padding: "10px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    width: "100%",
    overflow: "hidden",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
      wordBreak: "break-word",
    },
    "& .MuiTablePagination-toolbar": {
      minHeight: "40px",
    },
    "& P.MuiTablePagination-selectLabel,P.MuiTablePagination-displayedRows": {
      marginBottom: "0px !important",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "35px",
      marginLeft: "-2px",
    },
  },
}));

const statusList = [
  { label: "Not Deployed", Name: "Not Deployed" },
  { label: "Deploying", Name: "Deploying" },
  { label: "Deployed", Name: "Deployed" },
  { label: "Deployment Failed", Name: "Deployment Failed" },
  { label: "Undeploying", Name: "Undeploying" },
  { label: "Undeployment Failed", Name: "Undeployment Failed" },
  { label: "Undetected", Name: "Undetected" },
];
const defaultStatusFilter = [
  "Not Deployed",
  "Deploying",
  "Deployed",
  "Deployment Failed",
  "Undeploying",
  "Undeployment Failed",
  "Undetected",
];

const breadcrumbArr = [
  { link: "/reports", displayName: "Reports", active: false },
  { link: "#", displayName: "Hosted App Usage", active: true },
];

const HostedAppUsage = () => {
  const { classes } = useStyles();
  const { height: tableHeight } = useWindowResize(200);

  const appImageConfig = useSelector((state) => state.config.appImageConfig);

  const [loading, setLoading] = useState(false);

  const [sageMakerData, setSageMakerData] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(defaultStatusFilter);

  const [reportState, setReportState] = useState({
    orderBy: "Name",
    order: "asc",
  });

  const { orderBy, order } = reportState;

  const getUsageReport = useCallback(async () => {
    setLoading(true);
    setSageMakerData([]);
    const appHosting = new App();
    const { success, data } = await appHosting.getUsageReport();
    if (success) setSageMakerData(data);
    setLoading(false);
  }, []);

  const handleEmptyValue = useCallback((value) => value === "-" || value === "", []);

  const handleSorting = useCallback((property) => {
    setReportState((prev) => {
      const isAsc = prev.orderBy === property && prev.order === "asc";
      return {
        orderBy: property,
        order: isAsc ? "desc" : "asc",
      };
    });
  }, []);

  // Filter data as per search value then sorting will be applied
  const $filteredData = useMemo(() => {
    return sageMakerData.filter(({ Status }) => selectedStatus.includes(Status));
  }, [sageMakerData, selectedStatus]);

  // Sorting data as per the selection applied in the table header
  const $sortedData = useMemo(
    () =>
      ($filteredData || []).sort((a, b) => {
        if (orderBy === "Deployed_On") {
          const _aValue = handleEmptyValue(a[orderBy]) ? new Date(0) : new Date(a[orderBy]);
          const _bValue = handleEmptyValue(b[orderBy]) ? new Date(0) : new Date(b[orderBy]);

          return order === "asc" ? _aValue - _bValue : _bValue - _aValue;
        }
        // else if (orderBy === "Status") {
        //   const _aValue = a["Status_Code"];
        //   const _bValue = b["Status_Code"];

        //   return order === "asc" ? _aValue - _bValue : _bValue - _aValue;
        // }
        else if (orderBy === "Estimated_Cost") {
          const _aValue = a[orderBy];
          const _bValue = b[orderBy];

          return order === "asc" ? _aValue - _bValue : _bValue - _aValue;
        } else {
          return order === "asc" ? a[orderBy]?.localeCompare(b[orderBy]) : b[orderBy]?.localeCompare(a[orderBy]);
        }
      }),
    [$filteredData, orderBy, order, handleEmptyValue]
  );

  const reportColumns = useMemo(() => {
    if (!appImageConfig?.length) return [];
    return [
      {
        id: "Name",
        name: "NAME",
        sortable: true,
        style: { minWidth: 160, wordBreak: "break-word" },
        align: "left",
        width: 160,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "WorkspaceName",
        name: "WORKSPACE",
        sortable: true,
        align: "left",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "Type",
        name: "TYPE",
        sortable: true,
        style: { minWidth: 120, wordBreak: "break-word" },
        align: "left",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          const Type = appImageConfig?.find((app) => app.type === record.Type);
          if (Type) {
            return (
              <Box className={classes.flexBox} sx={{ maxWidth: "fit-content" }}>
                <img src={`/images/${Type?.icon}`} alt={Type?.type} style={{ width: "20px", marginRight: "10px" }} />
                {Type?.type}
              </Box>
            );
          }
          return "";
        },
      },
      {
        id: "Status",
        name: "STATUS",
        sortable: true,
        style: { minWidth: 120, wordBreak: "break-word" },
        align: "left",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "ModifiedDate",
        name: `LAST MODIFIED`,
        sortable: true,
        style: { minWidth: 165, wordBreak: "break-word" },
        align: "center",
        width: 165,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (val ? convertTimezone(val) : "-"),
      },
      {
        id: "ModifiedBy",
        name: "MODIFIED BY",
        sortable: true,
        style: { minWidth: 150, wordBreak: "break-word" },
        align: "left",
        width: 150,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "Access_URL",
        name: "ACCESS URL",
        sortable: true,
        style: { minWidth: 150, wordBreak: "break-word" },
        align: "left",
        width: 150,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          if (record.Access_URL) {
            const linkToRedirect = `${record.Access_URL}/`;
            if (record.Status === "Deployed") return <Link>{linkToRedirect}</Link>;
            else return linkToRedirect;
          }
          return "";
        },
      },
      {
        id: "Auth_Type",
        name: "AUTH TYPE",
        sortable: true,
        style: { minWidth: 140, wordBreak: "break-word" },
        align: "left",
        width: 140,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "CPU_RAM_Usage",
        name: "CPU/RAM USAGE",
        sortable: true,
        style: { minWidth: 180, wordBreak: "break-word" },
        align: "left",
        width: 180,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "Estimated_Cost",
        name: "ESTIMATED COST",
        sortable: true,
        style: { minWidth: 180, wordBreak: "break-word" },
        align: "left",
        width: 180,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          return record.Estimated_Cost ? `$${record.Estimated_Cost} USD/Month` : "-";
        },
      },
    ];
  }, [appImageConfig, classes.flexBox]);

  useEffect(() => {
    getUsageReport();
    return () => {};
  }, [getUsageReport]);

  const handleStatusChange = (selectionList) => {
    setSelectedStatus(selectionList);
  };

  const handleCellClick = useCallback((record, colId) => {
    if (colId === "Access_URL" && record.Status === "Deployed")
      window.open(record.Access_URL + "/", "_blank", "noopener,noreferrer");
  }, []);

  return (
    <CommonLayout title="Hosted App Usage" breadcrumbArr={breadcrumbArr}>
      <Container maxWidth="false" sx={{ m: 0 }} className={classes.PanelContainer}>
        <Toolbar title="Hosted App Usage" helpText="Hosted App Usage">
          <MultiSelectDropDown
            dataid="id"
            variant="standard"
            placeHolder={<MultiSelect title="Status" dataLength={selectedStatus.length} />}
            options={statusList}
            selected={selectedStatus}
            isSelectAll={true}
            disabled={loading}
            onSelectChange={handleStatusChange}
            basedOn="Name"
            allowSearching
          />
          <Box>
            <Tooltip placement="top" title="Refresh" arrow>
              <span>
                <IconButton disabled={loading} onClick={getUsageReport}>
                  <RefreshOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Toolbar>

        <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
          <KMKTable
            loading={loading}
            columns={reportColumns}
            containerHeight={tableHeight}
            noDataText={"No records found"}
            data={$sortedData}
            sortingProperties={{ order, orderBy, handleSorting }}
            boldHeader
            tableProps={{ stickyHeader: true }}
            paginationProperties={{}}
            dataIdKey="App_id"
            onCellClick={handleCellClick}
          />
        </Paper>
      </Container>
    </CommonLayout>
  );
};

export default HostedAppUsage;
